import { Typography } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import { Box } from '@mui/system';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import NotFound from '../components/NotFound/NotFoundPage';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {
  handleTimeFilter,
  handleStatusFilter,
  searchDataByKey,
  priorAuthorizationArrayConvertor,
  extractValueFromCoding,
  getIconFromSubItemsArray,
  formatDateInMMDDYYYYWithComma,
} from '../shared/helper';
import {
  priorAuthorizationFilterDatas,
  DEFAULT_NULL_REPLACEMENT,
  dateFilterMenuItems,
} from '../shared/constants';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useSnackbar } from 'notistack';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  FETCH_ALL_PRIOR_AUTHORIZATION_QUERY,
  FETCH_PRIOR_AUTHORIZATION_BY_ID,
} from '../services/PriorAuthorizationQueries/PriorAuthorizationService';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import ApplicationContext from '../context/applicationContext';
import {
  PRIOR_AUTHORIZATION,
  PRIOR_AUTHORIZATION_TABLE_HEADING as tableHeading,
  PRIOR_AUTHORIZATION_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

const PriorConsent = () => {
  return (
    <FormGroup>
      <FormControlLabel
        control={<Checkbox />}
        label="I hereby give my consent to speed-up the authorization process"
      />
    </FormGroup>
  );
};

export const PriorAuthorization = () => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailsData, setDetailsData] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();

  const allPriorAuthorization = useQuery(FETCH_ALL_PRIOR_AUTHORIZATION_QUERY, {
    variables: { sourceUrl: '' },
  });

  const [fetchPriorAuthorizationById, fetchPriorAuthorizationByIdCall] =
    useLazyQuery(FETCH_PRIOR_AUTHORIZATION_BY_ID, {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(PRIOR_AUTHORIZATION.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchPriorAuthorizationById);
      },
    });

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
    },
  };

  const statusFilterOptions = {
    options: priorAuthorizationFilterDatas,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
    },
  };
  const loading = allPriorAuthorization.loading;

  useEffect(() => {
    if (allPriorAuthorization.error) {
      enqueueSnackbar(PRIOR_AUTHORIZATION.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
        variant: 'error',
      });
    }
  }, [allPriorAuthorization.error, enqueueSnackbar]);

  useEffect(() => {
    if (allPriorAuthorization.data) {
      let result = priorAuthorizationArrayConvertor(
        allPriorAuthorization.data.fetchAllPriorAuthorization
      );

      setRecordedData(result);
      setFilteredData(result);
    }
  }, [allPriorAuthorization.data]);

  const showFlyout = result => {
    const parseResult = JSON.parse(result.serviceRequestResource);
    const Name = extractValueFromCoding(parseResult?.code);
    const Code = parseResult?.code?.coding?.[0]?.code || '';
    const DiagnosisName =
      parseResult?.reasonCode?.[0]?.coding?.[0]?.display || '';
    const DiagnosisCode = parseResult?.reasonCode?.[0]?.coding?.[0]?.code || '';

    const LocationCode =
      parseResult?.locationCode?.[0]?.coding?.[0]?.code || '';
    const LocationName = parseResult?.locationReference?.[0]?.display || '';

    const modalData = [
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.SERVICE,
        value: Name + ' (' + Code + ')' || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.REQUESTING_PROVIDER,
        value: parseResult?.requester?.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.REQUEST_PRIORITY,
        value: parseResult?.priority || DEFAULT_NULL_REPLACEMENT,
      },

      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.REQUESTED_DATE,
        value:
          formatDateInMMDDYYYYWithComma(parseResult?.authoredOn) ||
          DEFAULT_NULL_REPLACEMENT,
      },

      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.DUE_DATE,
        value:
          formatDateInMMDDYYYYWithComma(parseResult?.occurrencePeriod?.start) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.STATUS,
        value: parseResult?.status || DEFAULT_NULL_REPLACEMENT,
      },

      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.SERVICE_LOCATION,
        value: LocationName + ', ' + LocationCode || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.SERVICE_START_DATE,
        value:
          formatDateInMMDDYYYYWithComma(parseResult?.occurrencePeriod?.start) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.SERVICE_END_DATE,
        value:
          formatDateInMMDDYYYYWithComma(parseResult?.occurrencePeriod?.end) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.DIAGNOSIS,
        value: DiagnosisCode + ': ' + DiagnosisName || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.UNITS,
        value: parseResult?.quantityQuantity?.unit || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.PRIOR_AUTH_ID,
        value: parseResult?.id || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.DOCUMENTS,
        value: '-',
        width: 12,
      },
      {
        column_name: PRIOR_AUTHORIZATION.MODAL_DATA.NOTES,
        value: parseResult?.note?.[0]?.text || DEFAULT_NULL_REPLACEMENT,
        width: 12,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = priorAuthId => {
    const { id } = priorAuthId;
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.name);
    fetchPriorAuthorizationById({
      variables: {
        priorAuthId: id,
      },
    });
  };

  React.useEffect(() => {
    const timeFilteredData = handleTimeFilter(
      timeFilter,
      recordsData,
      'requestDate'
    );
    const statusFilteredData = handleStatusFilter(
      statusFilter,
      timeFilteredData,
      priorAuthorizationFilterDatas
    );
    const searchData = searchDataByKey(statusFilteredData, 'name', searchValue);
    setFilteredData(searchData);
  }, [timeFilter, statusFilter, searchValue, recordsData]);
  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{PRIOR_AUTHORIZATION.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={PRIOR_AUTHORIZATION.SEARCH_PLACEHOLDER_TEXT}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>

      <Box sx={table}>
        {!loading && !filteredData.length ? (
          <NotFound title={PRIOR_AUTHORIZATION.TITLE_TEXT} />
        ) : (
          <ReusableTable
            tableHeading={tableHeading}
            data={filteredData}
            viewDetailsHandler={viewDetailsHandler}
          />
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Financial?.subItems,
                'Prior Authorization'
              )
            : null
        }
        category={PRIOR_AUTHORIZATION.TITLE_TEXT}
        title={modalTitle}>
        <PriorConsent />
      </ServiceFlyout>
      <SimpleBackdrop
        open={loading || fetchPriorAuthorizationByIdCall.loading}
      />
    </Content>
  );
};
